import {
  mdiAccountCircleOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiLifebuoy,
  mdiLogout,
  mdiShieldAccountOutline,
} from "@mdi/js";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { api, ORG_ADMIN_ROLE } from "./api.ts";
import { Icon } from "./Icon.tsx";
import { messages } from "./i18n.ts";

/**
 * The application chrome.
 *
 * The bar carries the company, a divider, and the product, and nothing else on
 * the left: a learner has one place to be, so navigation between screens is not
 * a thing the bar needs to offer. Everything that is not their training hangs
 * off the account button instead, where organisation, preferences and
 * administration read as a different mode rather than as peers of the training
 * itself.
 */

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

function Avatar({ name, size }: { name: string; size: "sm" | "lg" }) {
  return (
    <span
      aria-hidden="true"
      className={`grid shrink-0 place-items-center rounded-full bg-primary font-bold text-primary-foreground ${
        size === "sm" ? "size-[26px] text-[11px]" : "size-9 text-[13px]"
      }`}
    >
      {initials(name)}
    </span>
  );
}

function MenuItem({
  icon,
  label,
  href,
  onClick,
  badge,
  active,
}: {
  icon: string;
  label: string;
  href?: string;
  onClick?: () => void;
  badge?: number | undefined;
  active?: boolean;
}) {
  const content = (
    <>
      <Icon className="text-muted-foreground" path={icon} />
      <span className="flex-1 text-left">{label}</span>
      {badge !== undefined && badge > 0 ? (
        <span className="pill bg-status-overdue-bg text-status-overdue-fg">{badge}</span>
      ) : null}
    </>
  );
  const className = `flex h-[34px] w-full items-center gap-2.5 rounded-md px-3 text-[13px] ${
    active ? "bg-accent font-semibold text-accent-foreground" : "text-foreground hover:bg-muted"
  }`;

  if (href) {
    return (
      <a className={className} href={href}>
        {content}
      </a>
    );
  }
  return (
    <button className={className} type="button" onClick={onClick}>
      {content}
    </button>
  );
}

export function AppBar({
  name,
  email,
  orgName,
  orgSlug,
  roles,
  onNavigate,
  onSignOut,
}: {
  name: string;
  email: string;
  orgName: string;
  orgSlug: string;
  roles: string[];
  onNavigate: (path: string) => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [organizations, setOrganizations] = useState<{ slug: string; name: string }[]>([]);
  const [switching, setSwitching] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState(false);
  const [outstanding, setOutstanding] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const canAdminister = roles.includes(ORG_ADMIN_ROLE);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onPointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    // Failing quietly is right: not being able to list organisations should
    // never stop somebody reaching their preferences or signing out.
    api
      .organizations()
      .then((result) => setOrganizations(result.organizations))
      .catch(() => {});
    if (canAdminister && outstanding === null) {
      api
        .overdue()
        .then((result) => setOutstanding(result.count))
        .catch(() => {});
    }
  }, [open, canAdminister, outstanding]);

  const others = organizations.filter((organization) => organization.slug !== orgSlug);

  return (
    <header className="sticky top-0 z-40 flex h-[52px] items-center gap-3.5 border-b border-border bg-card px-4 sm:px-5">
      <span className="text-[15px] font-bold tracking-tight text-foreground">
        {messages.common.brand}
      </span>
      <span aria-hidden="true" className="h-[22px] w-px bg-border" />
      <span className="hidden text-[13px] text-foreground sm:block">
        {messages.appBar.productName}
      </span>
      <nav aria-label="Workspaces" className="hidden items-center gap-1 md:flex">
        <button
          type="button"
          className="min-h-11 rounded-md px-3 text-sm font-semibold hover:bg-muted"
          onClick={() => onNavigate("/")}
        >
          My training
        </button>
        {canAdminister ? (
          <button
            type="button"
            className="min-h-11 rounded-md px-3 text-sm font-semibold hover:bg-muted"
            onClick={() => onNavigate("/admin")}
          >
            Administration
          </button>
        ) : null}
      </nav>
      <span
        className="ml-auto min-w-0 max-w-36 truncate text-xs font-semibold text-foreground sm:max-w-56"
        title={orgName}
      >
        {orgName}
      </span>

      <div className="flex-1" />

      <div className="relative" ref={containerRef}>
        <button
          aria-controls={menuId}
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label={messages.appBar.accountMenu}
          className="flex min-h-11 items-center gap-2 rounded-md border border-border bg-muted py-1 pl-1 pr-2.5 sm:min-h-0"
          type="button"
          onClick={() => setOpen((current) => !current)}
        >
          <Avatar name={name} size="sm" />
          <span className="hidden max-w-40 truncate text-[12px] text-foreground sm:block">
            {name}
          </span>
          <Icon
            className="text-muted-foreground"
            path={open ? mdiChevronUp : mdiChevronDown}
            size={16}
          />
        </button>

        {open ? (
          <div
            className="absolute right-0 top-[calc(100%+4px)] z-50 w-[min(296px,calc(100vw-1.5rem))] overflow-hidden rounded-lg border border-border bg-card shadow-lg"
            id={menuId}
          >
            <div className="flex items-center gap-2.5 border-b border-border px-4 py-3.5">
              <Avatar name={name} size="lg" />
              <div className="min-w-0">
                <p className="truncate text-[13px] font-bold text-foreground">{name}</p>
                <p className="truncate text-[11px] text-muted-foreground">{email}</p>
              </div>
            </div>

            <div className="border-b border-border px-4 pb-2 pt-2.5">
              <p className="meta-label">{messages.account.organization}</p>
              <p className="mt-1.5 text-[13px] font-semibold text-foreground">{orgName}</p>
              {others.length === 0 ? (
                <p className="mb-1.5 mt-1 text-[11px] text-muted-foreground">
                  {messages.account.onlyMembership}
                </p>
              ) : (
                <>
                  <p className="mb-1 mt-1 text-[11px] text-muted-foreground">
                    {messages.account.alsoMemberOf(others.map((other) => other.name))}
                  </p>
                  <ul className="mb-1.5">
                    {others.map((other) => (
                      <li key={other.slug}>
                        <button
                          className="flex h-[34px] w-full items-center gap-2 rounded-md px-1 text-[13px] text-foreground hover:bg-muted disabled:opacity-50"
                          disabled={switching !== null}
                          type="button"
                          onClick={async () => {
                            setSwitching(other.slug);
                            setSwitchError(false);
                            try {
                              await api.switchOrganization(other.slug);
                              // Reloaded rather than patched in place: the
                              // session now scopes every request to a different
                              // organisation, and anything still on screen
                              // belongs to the previous one.
                              window.location.assign("/");
                            } catch {
                              setSwitching(null);
                              setSwitchError(true);
                            }
                          }}
                        >
                          <span className="flex-1 truncate text-left">{other.name}</span>
                          <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-primary">
                            {switching === other.slug
                              ? messages.account.switching
                              : messages.account.switch}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {switchError ? (
                <p className="mb-1.5 text-[11px] text-destructive" role="alert">
                  {messages.account.switchError}
                </p>
              ) : null}
            </div>

            <div className="p-2">
              <MenuItem
                icon={mdiAccountCircleOutline}
                label={messages.account.profile}
                onClick={() => {
                  close();
                  onNavigate("/profile");
                }}
              />
            </div>

            {/*
              Offered only to somebody holding the grant. Every administrative
              route is a separate plugin that reads the grant per request and
              answers 404 on its own; this only stops showing a door that will
              not open.
            */}
            {canAdminister ? (
              <div className="border-t border-border p-2">
                <p className="meta-label px-3 pb-1.5 pt-0.5">{messages.account.administration}</p>
                <MenuItem
                  badge={outstanding ?? undefined}
                  icon={mdiShieldAccountOutline}
                  label={messages.account.compliance}
                  onClick={() => {
                    close();
                    onNavigate("/admin");
                  }}
                />
              </div>
            ) : null}

            <div className="border-t border-border p-2">
              <MenuItem
                href={messages.support.mailto}
                icon={mdiLifebuoy}
                label={messages.account.help}
              />
              <MenuItem icon={mdiLogout} label={messages.account.signOut} onClick={onSignOut} />
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
}
